import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  TableCell,
  TableBody,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  Container,
} from "@mui/material";

import { sendDataWithPost } from "../../../services/services";
import { getDownloadReportPSN } from "../../../services/reports";
import { COLORS } from "../../../utils/colors";
import Scrollbar from "../../../components/table/Scrollbar";
import { StyledTableRow } from "../../../components/TableRow";
import { isEmpty } from "lodash";
import FormSearch from "../../../components/FormSearch";
import CircularLoader from "../../../components/circularLoader";
import Pagination from "../../../components/Pagination";
import { LoadingButton } from "@mui/lab";
import { FileDownload } from "@mui/icons-material";
import HeaderHelpPage from "../../../components/HeaderHelpPage";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  const pad = (num) => num.toString().padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const currentYear = new Date().getFullYear();
const defaultValue = {
  year: currentYear,
};

const TABLE_HEAD = [
  { id: "report_type", label: "Report Type" },
  { id: "report_period", label: "Report Period" },
  { id: "created_at", label: "Created Date" },
  { id: "action", label: "Action" },
];

export default function MASReports() {
  const [loadingStates, setLoadingStates] = React.useState({});
  const [loaded, setLoaded] = useState(false);
  const [tableData, setTableData] = useState({
    results: [],
  });
  const [filter, setFilter] = useState(defaultValue);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50 });

  const handleChangePage = (event, newPage) => {
    queryPage(
      {
        page: newPage + 1,
        page_size: pagination.page_size,
      },
      filter
    );
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage(
      {
        page: 1,
        page_size: event.target.value,
      },
      filter
    );
  };

  const renderData = (res) => {
    const newData = res.results.map((item) => item);
    setTableData({
      ...res,
      results: newData,
    });
  };

  const queryPage = (params, payload = filter) => {
    loaded && setLoaded(false);
    const queryParams = new URLSearchParams(params || pagination).toString();
    const url = `/payout/report_psn/filter/?${queryParams}`;
    sendDataWithPost(url, payload, renderData, setLoaded, () => {
      params && setPagination(params);
    });
  };

  const onFilterSubmit = (fParams) => {
    const newParams = {
      ...fParams,
      year: fParams?.year != null ? String(fParams.year).trim() : "",
    };

    const newPagination = {
      ...pagination,
      page: 1,
    };

    setFilter(newParams);
    queryPage(newPagination, newParams);
  };

  useEffect(() => {
    onFilterSubmit(defaultValue);
  }, []);

  const isUserNotFound = tableData?.results?.length === 0;

  const schema = [
    {
      type: "year",
      label: "Year",
      param: "year",
    },
  ];

  const handleDownload = async (row) => {
    if (!row.id) return;
    setLoadingStates((prev) => ({ ...prev, [row.id]: true }));
    getDownloadReportPSN(
      row.id,
      `${row.report_type}_${row.report_period || row.report_year}.zip`,
      () => {
        setLoadingStates((prev) => ({ ...prev, [row.id]: false }));
      }
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <HeaderHelpPage title="MAS Reports" isShowLink={false} />
      <Box>
        <FormSearch
          schema={schema}
          onSearch={onFilterSubmit}
          defaultValue={defaultValue}
        />
        {loaded ? (
          <Card>
            <Scrollbar>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {TABLE_HEAD.map((headCell) => (
                        <TableCell key={headCell.id}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {headCell.label}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(tableData?.results || []).map((row) => {
                      const {
                        id,
                        report_period,
                        report_type,
                        is_released,
                        created_at,
                      } = row;

                      return (
                        <StyledTableRow hover key={id}>
                          <TableCell sx={{ width: "25%" }}>
                            <Typography variant="body2">{report_type}</Typography>
                          </TableCell>
                          <TableCell sx={{ width: "25%" }}>
                            <Typography variant="body2">{report_period}</Typography>
                          </TableCell>
                          <TableCell sx={{ width: "25%" }}>
                            <Typography variant="body2">
                              {created_at ? formatDate(created_at) : ""}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ width: "25%" }}>
                            <LoadingButton
                              variant="outlined"
                              onClick={() => handleDownload(row)}
                              loading={loadingStates[id] || false}
                              size="small"
                              startIcon={<FileDownload />}
                              disabled={loadingStates[id] || false}
                            >
                              Download
                            </LoadingButton>
                          </TableCell>
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                  {isUserNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                          <Typography
                            gutterBottom
                            align="center"
                            variant="subtitle1"
                          >
                            Not found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>
            {!isEmpty(tableData?.results) && (
              <Pagination
                count={tableData.count}
                pagination={pagination}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </Card>
        ) : (
          <CircularLoader />
        )}
      </Box>
    </Container>
  );
}
